import { useAuthStore } from '@/store/useAuthStore'
import { useUserTeams } from '@/hooks/useUserTeams'
import { useTeamRole } from '@/hooks/useTeamRole'
import { useMemo, useEffect } from 'react'

/**
 * Hook unifié pour accéder au contexte utilisateur et équipe
 * Combine useAuthStore, useUserTeams et useTeamRole
 * 
 * ✅ Fetch UNE SEULE FOIS grâce au cache React Query
 * ✅ Gère automatiquement l'initialisation de l'équipe active
 * 
 * @returns {Object} {
 *   user, activeTeamId, activeTeamRole, globalRole, effectiveRole,
 *   activeTeam, teams, switchTeam, isLoading
 * }
 */
export function useTeamContext() {
  // React Compiler + zustand : évite useCallback / dispatcher null (invalid hook call).
  'use no memo';
  const {
    user, 
    activeTeamId, 
    activeTeamRole,
    globalRole,
    switchTeam,
    initActiveTeam
  } = useAuthStore()
  const impersonating = useAuthStore((state) => state.impersonating)
  
  // ✅ Fetch UNE SEULE FOIS
  const { data: teams = [], isLoading: teamsLoading } = useUserTeams()
  
  // ✅ Fetch UNE SEULE FOIS par équipe (cache)
  const { data: teamRole, isLoading: roleLoading } = useTeamRole(activeTeamId)
  
  // Synchroniser le rôle depuis React Query vers Zustand
  useEffect(() => {
    if (teamRole && activeTeamId) {
      useAuthStore.setState({ activeTeamRole: teamRole })
    }
  }, [teamRole, activeTeamId])
  
  // Rôle effectif : super-admin ignore le contexte équipe (reste du rôle impersonation sinon)
  const effectiveRole = useMemo(() => {
    if (globalRole === 'super-admin') {
      return globalRole
    }
    return activeTeamRole || teamRole || globalRole
  }, [activeTeamRole, teamRole, globalRole])
  
  // Team active avec validation
  const activeTeam = useMemo(() => {
    if (!teams || teams.length === 0) return null
    if (activeTeamId) {
      const found = teams.find(t => t.id === activeTeamId)
      if (found) return found
    }
    // Par défaut, prendre la première équipe
    return teams[0] || null
  }, [teams, activeTeamId])
  
  // initActiveTeam : source unique ici (pas AppLayout / postAuthRedirect en impersonation)
  useEffect(() => {
    if (
      !activeTeamId &&
      !teamsLoading &&
      teams.length > 0 &&
      globalRole !== 'super-admin' &&
      !impersonating
    ) {
      initActiveTeam()
    }
  }, [activeTeamId, teamsLoading, teams.length, initActiveTeam, globalRole, impersonating])
  
  // Si activeTeamId existe mais n'est pas dans la liste des équipes, réinitialiser
  useEffect(() => {
    if (activeTeamId && teams.length > 0) {
      const found = teams.find(t => t.id === activeTeamId)
      if (!found && teams[0]) {
        // L'équipe active n'est plus valide, utiliser la première disponible
        switchTeam(teams[0].id)
      }
    }
  }, [activeTeamId, teams, switchTeam])
  
  return {
    user,
    activeTeamId,
    activeTeamRole: activeTeamRole || teamRole,
    globalRole,
    effectiveRole,
    activeTeam,
    teams,
    switchTeam,
    initActiveTeam,
    isLoading: teamsLoading || roleLoading,
  }
}
